import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, tap } from 'rxjs/operators';
import { ERROR_CODE_INVALID_CONFIGURATION } from 'src/app/shared/enums/error-code.enums';
import { initializeFailedAction } from '../initialize/initialize.actions';
import { DOCUMENT } from '../injection-token/document/document';
import { WINDOW } from '../injection-token/window/window';
import { TemporaryLoggerService } from '../temporary-logger/temporary-logger.service';
import { loadedParentConfigAction, setParentConfig, setParentConfigErrorAction } from './parent-config.actions';
import { setBugnsnagClientConfig } from './parent-config.utils';

@Injectable({
  providedIn: 'root',
})
export class ParentConfigEffects {
  /**
   * Define Bugsnag metadata
   */
  setParentConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setParentConfig, setParentConfigErrorAction),
      tap(({ payload, sessionId }) => {
        setBugnsnagClientConfig(sessionId, payload.user?.customerId);
      }),
      map(() => loadedParentConfigAction())
    )
  );

  /**
   * Handles errors with parent configs
   */
  setParentConfigError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setParentConfigErrorAction),
      map(({ errorCode, sessionId = '', payload }) =>
        initializeFailedAction({
          errorCode: errorCode || ERROR_CODE_INVALID_CONFIGURATION,
          reason: 'Invalid parent configs',
          metaData: {
            sessionId,
            parentConfig: `${JSON.stringify(payload)}`,
          },
        })
      )
    )
  );

  /**
   * Modify application theme with css variables
   */
  overrideCssVariables$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setParentConfig),
        filter(({ payload }) => !!payload.cssVariables),
        map(({ payload }) => {
          try {
            const stylesConfig = payload?.cssVariables;
            Object.keys(stylesConfig).forEach((key) => {
              if (typeof this.document.documentElement?.style?.setProperty === 'function') {
                // need to remove this validation for simple-checkout since we already have the ajv validation
                if (typeof stylesConfig[key] === 'string') {
                  this.document.documentElement.style.setProperty(`--${key}`, stylesConfig[key]);
                }
              }
            });
          } catch (ignoreError) {}
        })
      ),
    { dispatch: false }
  );

  /**
   * @param actions$ Our actions stream
   */
  constructor(
    @Inject(WINDOW) private window: Window,
    @Inject(DOCUMENT) private document: Document,
    private actions$: Actions,
    private tempLogger: TemporaryLoggerService
  ) {}
}
